import { Subject, take } from 'rxjs';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import * as i0 from '@angular/core';
import { NgModule } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

class LetsMFAService {
    constructor(options, http, activatedRoute, router) {
        this.options = options;
        this.http = http;
        this.activatedRoute = activatedRoute;
        this.router = router;
        this.context = '/.well-known/letsmfa-client';
        this.mfaStatusChanged = new Subject();
    }
    /** Starts the Auth or Enroll flows based on the current user's status. */
    startEnrollOrAuth(options) {
        var _a, _b;
        this.http
            .get(((_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.baseUrl) !== null && _b !== void 0 ? _b : '') +
            this.context +
            '/authOrEnroll' +
            ((options === null || options === void 0 ? void 0 : options.responseUrl)
                ? '?responseUrl=' + encodeURIComponent(options.responseUrl)
                : ''), options === null || options === void 0 ? void 0 : options.httpOptions)
            .subscribe((data) => {
            window.location.href = data.url;
        });
    }
    /** Starts the Enroll flow for the current user. */
    startEnroll(options) {
        var _a, _b;
        this.http
            .get(((_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.baseUrl) !== null && _b !== void 0 ? _b : '') +
            this.context +
            '/enroll' +
            ((options === null || options === void 0 ? void 0 : options.responseUrl)
                ? '?responseUrl=' + encodeURIComponent(options.responseUrl)
                : ''), options === null || options === void 0 ? void 0 : options.httpOptions)
            .subscribe({
            next: (data) => {
                window.location.href = data.url;
            },
            error: (err) => {
                console.log(err);
            },
        });
    }
    /** Starts the Auth flow for the current user. */
    startAuth(options) {
        var _a, _b;
        this.http
            .get(((_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.baseUrl) !== null && _b !== void 0 ? _b : '') +
            this.context +
            '/auth' +
            ((options === null || options === void 0 ? void 0 : options.responseUrl)
                ? '?responseUrl=' + encodeURIComponent(options.responseUrl)
                : ''), options === null || options === void 0 ? void 0 : options.httpOptions)
            .subscribe({
            next: (data) => {
                window.location.href = data.url;
            },
            error: (err) => {
                console.log(err);
            },
        });
    }
    /** Returns an observable that will return the current user's validation State.
     * The validation state can be one of the following:
     * - "validated": The user has successfully completed the MFA flow.
     * - "invalid": The user has an invalid MFA token.
     * - "expired": The user's MFA token has expired.
     * - "not-present": The user has not yet completed the MFA flow.
     */
    checkValidationState(httpOptions) {
        if (this.currentCheckStateObservable) {
            return this.currentCheckStateObservable;
        }
        const url = new URL(window.location.href);
        const params = url.searchParams;
        if (params.has('responseToken')) {
            const subject = this.handleResponseToken(params.get('responseToken'));
            this.currentCheckStateObservable = subject;
            return subject;
        }
        return this._checkValidationState(httpOptions);
    }
    logout(httpOptions) {
        var _a, _b;
        const observable = this.http.get(((_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.baseUrl) !== null && _b !== void 0 ? _b : '') + this.context + '/logout', Object.assign(Object.assign({}, httpOptions), { withCredentials: true }));
        observable.pipe(take(1)).subscribe({
            next: (_response) => {
                this.mfaStatusChanged.next('not-present');
            },
            error: (err) => {
                console.error("Error logging out: ", err);
                // manually delete the cookie
                let expires = null;
                const d = new Date();
                d.setTime(d.getTime() - 2 * 24 * 60 * 60 * 1000);
                expires = "expires=" + d.toUTCString();
                document.cookie = 'letsmfa' + "=to-be-deleted;" + expires + ";path=/";
                this.mfaStatusChanged.next('not-present');
            }
        });
        return observable;
    }
    /** Ignores currentCheckStateObservable and performs the check task. */
    _checkValidationState(httpOptions) {
        var _a, _b;
        const observable = this.http.get(((_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.baseUrl) !== null && _b !== void 0 ? _b : '') + this.context + '/check', Object.assign(Object.assign({}, httpOptions), { withCredentials: true }));
        observable.pipe(take(1)).subscribe({
            next: (response) => {
                this.mfaStatusChanged.next(response.validationState);
                this.currentCheckStateObservable = undefined;
            },
            error: (err) => {
                this.mfaStatusChanged.next('invalid');
                this.currentCheckStateObservable = undefined;
            },
        });
        this.currentCheckStateObservable = observable;
        return observable;
    }
    handleResponseToken(responseToken) {
        var _a, _b;
        const newParams = this.activatedRoute.snapshot.params;
        delete newParams['responseToken'];
        const subject = new Subject();
        this.http
            .get(((_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.baseUrl) !== null && _b !== void 0 ? _b : '') +
            this.context +
            '/response?responseToken=' +
            encodeURIComponent(responseToken), {
            headers: {
                Accept: 'application/json',
            },
            withCredentials: true,
        })
            .pipe(take(1))
            .subscribe({
            next: () => {
                this._checkValidationState()
                    .pipe(take(1))
                    .subscribe({
                    next: (response) => {
                        subject.next(response);
                        subject.complete();
                    },
                });
            },
            error: (err) => {
                this.mfaStatusChanged.next('invalid');
                subject.error(err);
                subject.complete();
            },
        });
        return subject;
    }
}

class LetsMFAModule {
    static forRoot(options) {
        return {
            ngModule: LetsMFAModule,
            providers: [
                {
                    provide: LetsMFAService,
                    useFactory: (http, activatedRoute, router) => new LetsMFAService(options, http, activatedRoute, router),
                    deps: [HttpClient, ActivatedRoute, Router],
                },
            ],
        };
    }
}
LetsMFAModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LetsMFAModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
LetsMFAModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LetsMFAModule, imports: [HttpClientModule] });
LetsMFAModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LetsMFAModule, imports: [[HttpClientModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LetsMFAModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [HttpClientModule],
                    exports: [],
                }]
        }] });

/*
 * Public API Surface of lets-mfa-lib-angular
 */

/**
 * Generated bundle index. Do not edit.
 */

export { LetsMFAModule, LetsMFAService };
